import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { GameSearch, type GameSelection } from '../components/GameSearch'
import { KeyBatchForm } from '../components/KeyBatchForm'
import { ManualGameForm } from '../components/ManualGameForm'
import { useImportGame } from '../lib/admin-api'

export const Route = createFileRoute('/admin/add')({
  component: AddKeysPage,
})

type Stage =
  | { step: 'search' }
  | { step: 'importing'; title: string }
  | { step: 'manual'; title: string }
  | { step: 'keys'; gameId: string; title: string }

/**
 * Search a game, pick it, paste keys.
 *
 * Two interactions from typing to pasting, whether the game is already in the
 * library or brand new — picking an unknown game imports its metadata and art
 * on the way to the same form, rather than making that a separate step.
 */
function AddKeysPage() {
  const [stage, setStage] = useState<Stage>({ step: 'search' })
  const [toast, setToast] = useState<string | null>(null)
  const importGame = useImportGame()

  const handleSelect = (selection: GameSelection) => {
    setToast(null)

    if (selection.kind === 'library') {
      setStage({ step: 'keys', gameId: selection.id, title: selection.title })
      return
    }

    setStage({ step: 'importing', title: selection.title })

    importGame.mutate(
      { provider: selection.provider, externalId: selection.externalId },
      {
        onSuccess: (game) => setStage({ step: 'keys', gameId: game.id, title: game.title }),
        onError: () => setStage({ step: 'search' }),
      },
    )
  }

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="font-display text-2xl font-semibold tracking-tight">Add keys</h1>
      <p className="mt-1 text-sm text-ink-soft">
        Find the game, then paste however many keys you have.
      </p>

      {toast ? (
        <p
          role="status"
          className="mt-5 rounded-lg border border-good/30 bg-good/10 px-4 py-3 text-sm text-good"
        >
          {toast}
        </p>
      ) : null}

      {importGame.isError ? (
        <p role="alert" className="mt-5 rounded-lg border border-bad/30 bg-bad/10 px-4 py-3 text-sm text-bad">
          {(importGame.error as Error).message}
        </p>
      ) : null}

      <div className="mt-6">
        {stage.step === 'search' ? (
          <GameSearch
            onSelect={handleSelect}
            onManual={(title) => setStage({ step: 'manual', title })}
            autoFocus
          />
        ) : stage.step === 'importing' ? (
          <div className="rounded-xl border border-line bg-surface-1 px-6 py-10 text-center">
            <p className="text-sm text-ink-soft">
              Fetching artwork and details for <span className="text-ink">{stage.title}</span>…
            </p>
          </div>
        ) : stage.step === 'manual' ? (
          <ManualGameForm
            initialTitle={stage.title}
            onCancel={() => setStage({ step: 'search' })}
            // Straight into the key form, same as an imported game — the
            // point of adding it was always the keys.
            onCreated={(game) => setStage({ step: 'keys', gameId: game.id, title: game.title })}
          />
        ) : (
          <KeyBatchForm
            gameId={stage.gameId}
            gameTitle={stage.title}
            onCancel={() => setStage({ step: 'search' })}
            onDone={({ added, skipped }) => {
              setToast(
                `Added ${added} ${added === 1 ? 'key' : 'keys'} to ${stage.title}` +
                  (skipped > 0 ? ` · ${skipped} duplicate${skipped === 1 ? '' : 's'} skipped` : ''),
              )
              // Straight back to the search box, ready for the next game.
              setStage({ step: 'search' })
            }}
          />
        )}
      </div>
    </div>
  )
}
